/**
 * F09 v2 (§13 row 21, §14 "Integrations") — the small verification pieces the
 * other admin modules embed: an inert tick, a table cell, and a card that
 * deep-links into the `/verifications` module.
 *
 * Rendering rule (§13): the tick shows if and only if the entity's boolean is
 * true. Admin ticks are always inert (`interactive={false}`) — no explainer
 * link, just a tooltip title. `verifiedAt` is only the hero "since" date.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import { Badge, Card, VerificationBadge, VerifiedTick } from '@bravemove/ui-web';
import type {
  OwnerVerificationState,
  VerifiableEntityType,
  VerificationRequestRef,
} from '@bravemove/types';
import { useVerificationPermissions } from '../../hooks/useVerificationPermissions';
import { VERIFICATION_LINK_CLASS, entityPath, requestPath } from './labels';

/** An inert inline tick, rendered only when `verified` is true. */
export function InertTick({
  verified,
  entityType,
  size = 'sm',
  testId,
}: {
  readonly verified: boolean | undefined;
  readonly entityType?: VerifiableEntityType | undefined;
  readonly size?: 'sm' | 'md' | 'lg';
  readonly testId?: string;
}) {
  if (verified !== true) return null;
  return (
    <span className="inline-flex" data-testid={testId}>
      <VerifiedTick entityType={entityType} size={size} interactive={false} />
    </span>
  );
}

/**
 * A table cell: the tick (when verified), the owner-state badge and an
 * "open request" marker. An absent state renders as `not_requested`.
 */
export function VerificationStateCell({
  entityType,
  state,
  verified,
  openRequest,
  testId,
}: {
  readonly entityType: VerifiableEntityType;
  readonly state: OwnerVerificationState | undefined;
  /** The per-entity boolean when the payload has one; else derived from `state`. */
  readonly verified?: boolean | undefined;
  readonly openRequest?: VerificationRequestRef | null | undefined;
  readonly testId?: string;
}) {
  const { t } = useI18n();
  const resolved: OwnerVerificationState = state ?? 'not_requested';
  const ticked = verified ?? resolved === 'verified';
  return (
    <span
      className="inline-flex flex-wrap items-center gap-1.5"
      data-testid={testId}
      data-state={resolved}
    >
      <InertTick verified={ticked} entityType={entityType} />
      <VerificationBadge status={resolved} size="sm" />
      {openRequest ? (
        <Badge tone="info" size="sm" data-testid={testId ? `${testId}-open-request` : undefined}>
          {t('admin:verification.integrations.openRequestBadge')}
        </Badge>
      ) : null}
    </span>
  );
}

export interface EntityVerificationCardProps {
  readonly entityType: VerifiableEntityType;
  readonly entityId: string;
  /** The per-entity boolean — drives the tick (§13). */
  readonly verified: boolean | undefined;
  /** Display-only "since" date for the hero tick. */
  readonly verifiedAt?: string | null | undefined;
  /** Owner-state badge; omitted when the payload carries none. */
  readonly state?: OwnerVerificationState | undefined;
  readonly openRequest?: VerificationRequestRef | null | undefined;
  /** False when the producer says the entity page is unavailable. */
  readonly available?: boolean;
  readonly testId?: string;
  readonly children?: ReactNode;
}

/**
 * A "Verification" card: the tick (hero) or "not verified", the owner-state
 * badge, and links to the entity page and the open request. The links need
 * `verifications:read`; without it the card is display-only.
 */
export function EntityVerificationCard({
  entityType,
  entityId,
  verified,
  verifiedAt,
  state,
  openRequest,
  available = true,
  testId = 'entity-verification-card',
  children,
}: EntityVerificationCardProps) {
  const { t } = useI18n();
  const { canRead } = useVerificationPermissions();
  const showLinks = canRead && available && entityId.length > 0;

  return (
    <Card data-testid={testId}>
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-[13px] font-semibold text-ink">
            {t('admin:verification.integrations.cardTitle')}
          </h3>
          {state ? <VerificationBadge status={state} size="sm" /> : null}
        </div>
        {verified === true ? (
          <div data-testid={`${testId}-tick`}>
            <VerifiedTick
              entityType={entityType}
              variant="hero"
              since={verifiedAt ?? null}
              interactive={false}
            />
          </div>
        ) : (
          <p className="text-[12.5px] text-ink-3" data-testid={`${testId}-not-verified`}>
            {t('admin:verification.entity.notVerified')}
          </p>
        )}
        {children}
        {showLinks ? (
          <div className="flex flex-wrap gap-2">
            <Link
              to={entityPath(entityType, entityId)}
              className={VERIFICATION_LINK_CLASS}
              data-testid={`${testId}-entity-link`}
            >
              {t('admin:verification.integrations.openEntity')}
            </Link>
            {openRequest ? (
              <Link
                to={requestPath(openRequest.requestId)}
                className={VERIFICATION_LINK_CLASS}
                data-testid={`${testId}-request-link`}
              >
                {t('admin:verification.common.openRequest')}
              </Link>
            ) : null}
          </div>
        ) : null}
      </div>
    </Card>
  );
}
